import { useCallback, useMemo } from 'react';
import {
    generateQueryParamsString,
    parseQueryParamsString,
    type QueryParamsObjectConfig,
} from '../query-params';
import { useAppState } from './use-app-state';
import { useLocalStorage } from './use-local-storage';
import type { SetValue } from './use-query-params';
import { useQueryParams } from './use-query-params';

/**
 * Like `useQueryParams`, but persists filter values to localStorage.
 * When the page is visited without URL query params the last-saved
 * filters are restored automatically.
 *
 * The storage key is automatically prefixed with the current user's ID
 * so each user gets their own persisted filters.
 *
 * Priority: URL params > localStorage > defaultValues
 */

export function usePersistedQueryParams<TValue extends Record<string, unknown>>(
    config: QueryParamsObjectConfig<TValue>,
    defaultValues: TValue,
    storageKey: string,
): [TValue, SetValue<TValue>] {
    const { platoUser } = useAppState();
    const fullStorageKey = `filters:${platoUser?.id}:${storageKey}`;
    const [storedQuery, setStoredQuery] = useLocalStorage<string | undefined>(fullStorageKey, undefined);

    const fallbackValue = useMemo(() => {
        if (storedQuery) {
            return parseQueryParamsString(new URLSearchParams(storedQuery), config, defaultValues);
        }

        return defaultValues;
    }, [defaultValues, storedQuery, config]);

    const [queryValue, setQueryValue] = useQueryParams(config, fallbackValue);

    const setValue: SetValue<TValue> = useCallback(
        (newValue, action) => {
            setQueryValue(newValue, action);

            const resolved = typeof newValue === 'function' ? newValue(queryValue) : newValue;
            setStoredQuery(generateQueryParamsString(config, resolved));
        },
        [setQueryValue, config, queryValue, setStoredQuery],
    );

    return [queryValue, setValue];
}
